import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Button } from "@/shared/components/form/Button";
import useTranslations from "@/shared/lang/useTranslations";

interface AuditoriaPaginationProps {
  currentPage: number;
  lastPage: number | null;
  canGoPrevious: boolean;
  canGoNext: boolean;
  isLoading?: boolean;
  pageNumbers: (number | string)[];
  from: number;
  to: number;
  total: number;
  onPageChange: (page: number) => void;
  recordsText?: React.ReactNode;
  totalPagesText?: string;
  firstPageTitle?: string;
  prevPageTitle?: string;
  nextPageTitle?: string;
  lastPageTitle?: string;
}

export const AuditoriaPagination = ({
  currentPage,
  lastPage,
  canGoPrevious,
  canGoNext,
  isLoading = false,
  pageNumbers,
  from,
  to,
  total,
  onPageChange,
  recordsText,
  totalPagesText,
  firstPageTitle,
  prevPageTitle,
  nextPageTitle,
  lastPageTitle,
}: AuditoriaPaginationProps) => {
  const { t } = useTranslations("global");

  const recordsContent =
    recordsText ?? t("pagination_records", { from, to, total });
  const resolvedFirstPageTitle = firstPageTitle ?? t("pagination_first_page");
  const resolvedPrevPageTitle = prevPageTitle ?? t("pagination_prev_page");
  const resolvedNextPageTitle = nextPageTitle ?? t("pagination_next_page");
  const resolvedLastPageTitle = lastPageTitle ?? t("pagination_last_page");
  const resolvedTotalPagesText =
    totalPagesText ??
    (lastPage != null ? t("pagination_total_pages", { lastPage }) : undefined);

  return (
    <div className="px-6 py-4 border-t border-gray-200 dark:border-zinc-800 flex items-center justify-between flex-wrap gap-4">
      <p className="text-sm text-gray-700 dark:text-gray-300">
        {recordsContent}
      </p>

      <div className="flex items-center justify-end flex-wrap gap-4">
        <div className="flex items-center gap-1">
          {lastPage && lastPage > 1 && (
            <Button
              variant="outline"
              onClick={() => onPageChange(1)}
              disabled={!canGoPrevious || isLoading}
              type="button"
              className="px-2 py-1 min-w-[32px] h-8 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 disabled:bg-gray-50 dark:disabled:bg-zinc-900 disabled:text-gray-400 dark:disabled:text-zinc-600"
              title={resolvedFirstPageTitle}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!canGoPrevious || isLoading}
            type="button"
            className="px-2 py-1 min-w-[32px] h-8 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 disabled:bg-gray-50 dark:disabled:bg-zinc-900 disabled:text-gray-400 dark:disabled:text-zinc-600"
            title={resolvedPrevPageTitle}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
            {pageNumbers.map((page, index) => {
              if (page === "...") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-2 text-gray-500 dark:text-gray-400"
                  >
                    ...
                  </span>
                );
              }

              const pageNum = page as number;
              const isActive = pageNum === currentPage;

              return (
                <Button
                  key={pageNum}
                  variant={isActive ? "primary" : "outline"}
                  onClick={() => !isActive && onPageChange(pageNum)}
                  disabled={isActive || isLoading}
                  type="button"
                  className={`min-w-[32px] h-8 ${
                    isActive
                      ? ""
                      : "bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600"
                  }`}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!canGoNext || isLoading}
            type="button"
            className="px-2 py-1 min-w-[32px] h-8 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 disabled:bg-gray-50 dark:disabled:bg-zinc-900 disabled:text-gray-400 dark:disabled:text-zinc-600"
            title={resolvedNextPageTitle}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {lastPage && lastPage > 1 && (
            <Button
              variant="outline"
              onClick={() => onPageChange(lastPage)}
              disabled={!canGoNext || isLoading}
              type="button"
              className="px-2 py-1 min-w-[32px] h-8 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 disabled:bg-gray-50 dark:disabled:bg-zinc-900 disabled:text-gray-400 dark:disabled:text-zinc-600"
              title={resolvedLastPageTitle}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          )}

        {lastPage && (
          <span className="text-sm text-gray-700 dark:text-gray-300 ml-2">
            {resolvedTotalPagesText}
          </span>
        )}
      </div>
    </div>
  );
};
